import { Component, OnInit, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { RouteService } from '../service/route.service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { AssigTripsService } from 'src/app/component/assigned-trips/assigntrips-service/assigntrips-service';
import * as moment from 'moment-timezone';

@Component({
  selector: 'app-assign-trip-modal',
  templateUrl: './assign-trip-modal.component.html',
  styleUrls: ['./assign-trip-modal.component.css']
})
export class AssignTripModalComponent implements OnInit {
  @Input() selectedDate: string | Date | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() onAssign = new EventEmitter<any>();

  assignForm: FormGroup;
  submitted = false;
  routes: any[] = [];
  filteredTrips: any[] = [];
  user: any;
  allStops: any[] = [];

  // Real data for trips
  trips: any[] = [];

  selectedTripId: string | null = null;

  // Dual route support
  pickupStops: any[] = [];
  dropoffStops: any[] = [];

  // Stored configurations for trips in the list
  tripConfigs: Map<string, any> = new Map();

  constructor(
    private fb: FormBuilder,
    private routeService: RouteService,
    private sharedDataService: SharedDataService,
    private assignTripService: AssigTripsService
  ) { }

  ngOnInit() {
    this.assignForm = this.fb.group({
      searchQuery: [''],
      routeId: [null, Validators.required],
      pickupStopId: [null, Validators.required],
      dropoffStopId: [null, Validators.required]
    });

    this.assignForm.get('routeId').valueChanges
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(routeId => {
        this.loadRouteStops(routeId);
      });

    this.assignForm.get('pickupStopId').valueChanges
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(pickupId => {
        this.filterDropoffStops(pickupId);
      });

    this.filteredTrips = this.trips;
    this.getUser();

    // Listen for search changes
    this.assignForm.get('searchQuery').valueChanges
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(val => {
        this.filterTrips(val);
      });
  }

  ngOnChanges(changes: any) {
    if (changes.selectedDate && !changes.selectedDate.firstChange) {
      this.fetchRoutes();
      this.fetchTrips();
    }
  }

  getUser() {
    this.sharedDataService.getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          this.fetchRoutes();
          this.fetchTrips();
        }
      });
  }

  fetchRoutes() {
    const tz = (this.user && this.user.timeZone) ? this.user.timeZone : moment.tz.guess();
    const referenceDate = this.selectedDate ? new Date(this.selectedDate) : new Date();
    const queryParams = {
      date: moment.tz(referenceDate, tz).startOf('day').toISOString()
    };

    this.routeService.getRoutes(queryParams)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        // Only show active routes for assignment
        this.routes = (data || []).filter(r => 
          r.status !== 'completed' && 
          r.status !== 'cancelled' && 
          r.status !== 'Completed' && 
          r.status !== 'Cancelled'
        );

        // Map already reservedAssigns from routes to our local staging state
        this.routes.forEach(route => {
          if (route.reservedAssigns && route.reservedAssigns.length) {
            route.reservedAssigns.forEach(ra => {
              // Only map if not already manually staged in this session
              if (!this.tripConfigs.has(ra.assign)) {
                this.tripConfigs.set(ra.assign, {
                  routeId: route._id,
                  pickupStopId: ra.onboard,
                  dropoffStopId: ra.offboard
                });
              }
            });
          }
        });
      });
  }

  fetchTrips() {
    const tz = (this.user && this.user.timeZone) ? this.user.timeZone : moment.tz.guess();

    // Use selectedDate if provided, fallback to today
    const referenceDate = this.selectedDate ? new Date(this.selectedDate) : new Date();

    // Using the specific range format requested by the user
    const startOfDay = moment.tz(referenceDate, tz).startOf('day').toISOString();
    const endOfDay = moment.tz(referenceDate, tz).endOf('day').toISOString();

    const query = {
      status: 'unassigned',
      scheduleTime: `${startOfDay},${endOfDay}`,
      isShowAllTrips: false,
      skipPagination: true
    };

    console.log('Fetching Unassigned Trips with query:', query);

    this.assignTripService.getAssignList(query)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        console.log('Unassigned Trips API Response:', data);
        if (data && data.length) {
          this.trips = data.map(t => {
            const passengerName = (t.memberDetail && t.memberDetail.displayName) ||
              (t.clientDetail && t.clientDetail.displayName) ||
              (t.priorityClient && t.priorityClient.displayName) ||
              (t.firstName ? (t.firstName + ' ' + (t.lastName || '')).trim() : null) ||
              t.clientName ||
              'Unknown Passenger';

            return {
              id: t.assignId || t._id,
              tripId: t.tripId,
              passengerName: passengerName,
              path: `${(t.jobOriginAddress && t.jobOriginAddress.split(',')[0]) || (t.pickupAddress && t.pickupAddress.split(',')[0]) || ''} → ${(t.jobDestinationAddress && t.jobDestinationAddress.split(',')[0]) || (t.dropoffAddress && t.dropoffAddress.split(',')[0]) || ''}`,
              seats: t.wheelChair ? 1 : 1,
              phone: (t.memberDetail && t.memberDetail.phoneNumber) || (t.clientDetail && t.clientDetail.phoneNumber) || t.phoneNumber || 'N/A',
              initials: passengerName !== 'Unknown Passenger' ? passengerName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : '??'
            };
          });
          this.filterTrips(this.assignForm.get('searchQuery').value);
        } else {
          this.trips = [];
          this.filteredTrips = [];
        }
      }, err => {
        console.error('Error fetching unassigned trips:', err);
      });
  }

  loadRouteStops(routeId: string) {
    const route = this.routes.find(r => r._id === routeId);
    if (route && route.stops) {
      // Only filter out departed stops as per request
      const stops = route.stops
        .filter((s: any) => s.status !== 'departed')
        .map((s, idx) => ({
          id: idx + 1,
          name: s.name || (s.address && s.address.split(',')[0]) || 'Unknown',
          time: (s.scheduledArrivalTime || s.time) ? moment(s.scheduledArrivalTime || s.time).format('HH:mm') : '00:00',
          originalIndex: idx // Keep track of chronological order
        }));
      
      this.allStops = [...stops];
      this.pickupStops = [...stops];
      
      // Initially, dropoff can be any stop after the first pickup
      this.filterDropoffStops(stops.length > 0 ? stops[0].id : null);

      if (stops.length >= 2) {
        this.assignForm.patchValue({
          pickupStopId: stops[0].id,
          dropoffStopId: stops[stops.length - 1].id
        }, { emitEvent: true });
      }
    } else {
      this.allStops = [];
      this.pickupStops = [];
      this.dropoffStops = [];
    }
  }

  filterDropoffStops(pickupId: number) {
    if (!pickupId || !this.allStops.length) {
      this.dropoffStops = [...this.allStops];
      return;
    }

    const pickupStop = this.allStops.find(s => s.id === pickupId);
    if (pickupStop) {
      // Dropoff must be strictly AFTER the pickup original index
      this.dropoffStops = this.allStops.filter(s => s.originalIndex > pickupStop.originalIndex);
      
      // If current dropoff is now invalid, auto-select the last stop
      const currentDropoffId = this.assignForm.get('dropoffStopId').value;
      const isStillValid = this.dropoffStops.some(s => s.id === currentDropoffId);
      
      if (!isStillValid && this.dropoffStops.length > 0) {
        this.assignForm.patchValue({
          dropoffStopId: this.dropoffStops[this.dropoffStops.length - 1].id
        }, { emitEvent: false });
      }
    }
  }

  filterTrips(query: string) {
    if (!query) {
      this.filteredTrips = this.trips;
      return;
    }
    const q = query.toLowerCase();
    this.filteredTrips = this.trips.filter(t =>
      t.passengerName.toLowerCase().includes(q) ||
      t.id.toLowerCase().includes(q) ||
      t.phone.toLowerCase().includes(q)
    );
  }

  selectTrip(tripId: string) {
    // 1. Save current configuration for the previously selected trip if valid
    if (this.selectedTripId && this.assignForm.valid) {
      this.tripConfigs.set(this.selectedTripId, { ...this.assignForm.value });
    }

    this.selectedTripId = tripId;

    // 2. Load or Clear form
    const existingConfig = this.tripConfigs.get(tripId);
    if (existingConfig) {
      this.assignForm.patchValue(existingConfig, { emitEvent: false });
      this.loadRouteStops(existingConfig.routeId);
    } else {
      // CLEAR form for new selection as requested
      this.assignForm.reset({ searchQuery: this.assignForm.get('searchQuery').value }, { emitEvent: false });
      this.pickupStops = [];
      this.dropoffStops = [];
    }
  }

  clearTripConfig(event: Event, tripId: string) {
    event.stopPropagation();
    this.tripConfigs.delete(tripId);

    // If we're clearing the currently selected trip, reset the form too
    if (this.selectedTripId === tripId) {
      this.assignForm.reset({ searchQuery: this.assignForm.get('searchQuery').value }, { emitEvent: false });
      this.pickupStops = [];
      this.dropoffStops = [];
    }
  }

  getTripConfig(tripId: string) {
    return this.tripConfigs.get(tripId);
  }

  getRouteName(routeId: string) {
    const route = this.routes.find(r => r._id === routeId);
    return route ? (route.routeNumber ? `#${route.routeNumber} ` : '') + route.routeName : '';
  }

  closeModal() {
    this.close.emit();
  }

  onSubmit() {
    this.submitted = true;

    // 1. Ensure current selection is captured in tripConfigs
    if (this.selectedTripId && this.assignForm.valid) {
      this.tripConfigs.set(this.selectedTripId, { ...this.assignForm.value });
    }

    if (this.tripConfigs.size === 0) {
      return;
    }

    // 2. Build grouped payload
    const routeMap = new Map<string, any[]>();

    this.tripConfigs.forEach((config, tripId) => {
      if (!routeMap.has(config.routeId)) {
        routeMap.set(config.routeId, []);
      }
      routeMap.get(config.routeId).push({
        assign: tripId,
        onboard: config.pickupStopId,
        offboard: config.dropoffStopId
      });
    });

    const payload = {
      data: Array.from(routeMap.entries()).map(([routeId, assigns]) => ({
        busTrip: routeId,
        reservedAssigns: assigns
      }))
    };

    // 3. API Call
    this.routeService.bulkUpdate(payload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(res => {
        this.onAssign.emit(res);
        this.closeModal();
      }, err => {
        console.error('Bulk update failed:', err);
      });
  }

  getSelectedTrip() {
    return this.trips.find(t => t.id === this.selectedTripId);
  }

  getStopName(stopId: number, type: 'pickup' | 'dropoff' = 'pickup') {
    const stops = type === 'pickup' ? this.pickupStops : this.dropoffStops;
    const stop = stops.find(s => s.id === stopId);
    return stop ? `${stop.name} (${stop.time})` : '...';
  }

  ngOnDestroy(): void { }

}
